import {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {jwtDecode} from 'jwt-decode';
import {
    chatWebSocketUrl,
    createConversation as createConversationRequest,
    createWebSocketTicket,
    ensurePropertyChannel as ensurePropertyChannelRequest,
    getChatBootstrap,
    getConversation,
    getUnreadStatus,
    listChatUsers,
    listConversationFeed,
    listConversationGroups,
    queryConversationSummaries,
    syncEvents,
} from '../../service/ChatService';
import {
    chatCacheScope,
    clearChatCacheScope,
    deleteConversationCache,
    readCachedChat,
    readCachedGroups,
    readCachedMessages,
    readEventCursor,
    writeBootstrapCache,
    writeCachedGroups,
    writeConversationCache,
    writeEventCursor,
    writeMessageCache,
} from './cache/chatCache';
import {getChatDeviceId} from './notifications/chatDevice';
import {useWebPushNotifications} from './notifications/useWebPushNotifications';

const ChatContext = createContext(null);

function tokenIdentity() {
    const token = localStorage.getItem('cosToken');
    if (!token) return '';
    try {
        const claims = jwtDecode(token.replace(/^Bearer\s+/i, ''));
        const apiUser = claims?.optinestUser?.optinestApiUser;
        return String(apiUser?.id || claims?.user_id || claims?.sub || apiUser?.username || claims?.username || '').toLowerCase();
    } catch {
        return '';
    }
}

function routeConversationId() {
    const match = window.location.pathname.match(/^\/chat\/(\d+)\/?$/);
    return Number(match?.[1] || 0);
}

function mergeConversationList(current, incoming) {
    const byId = new Map(current.map(item => [item.id, item]));
    for (const item of incoming || []) {
        const previous = byId.get(item.id);
        byId.set(item.id, previous?.details_loaded && !item.details_loaded
            ? {...previous, ...item, members: previous.members, context: previous.context, details_loaded: true}
            : {...previous, ...item});
    }
    return [...byId.values()].toSorted((left, right) => {
        const updated = new Date(right.updated_at || 0).getTime() - new Date(left.updated_at || 0).getTime();
        return updated || right.id - left.id;
    });
}

function chunked(values, size) {
    const chunks = [];
    for (let index = 0; index < values.length; index += size) chunks.push(values.slice(index, index + size));
    return chunks;
}

export function ChatProvider({children}) {
    const scopeRef = useRef(chatCacheScope(tokenIdentity()));
    const [users, setUsers] = useState([]);
    const [adminUsers, setAdminUsers] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);
    const [conversations, setConversations] = useState([]);
    const [conversationGroups, setConversationGroups] = useState([]);
    const [connectionStatus, setConnectionStatus] = useState('connecting');
    const [error, setError] = useState(null);
    const [invalidations, setInvalidations] = useState({});
    const [hasUnread, setHasUnread] = useState(false);
    const [nextCursor, setNextCursor] = useState('');
    const [hasMoreConversations, setHasMoreConversations] = useState(false);
    const conversationsRef = useRef([]);
    const conversationDetailPromisesRef = useRef(new Map());
    const conversationGroupsLoadedRef = useRef(false);
    const conversationGroupsPromiseRef = useRef(null);
    const bootstrapPromiseRef = useRef(null);
    const summaryRefreshTimerRef = useRef(null);
    const pendingSummaryIdsRef = useRef(new Set());
    const socketRef = useRef(null);
    const activeConversationRef = useRef(null);
    const notifications = useWebPushNotifications();

    const updateConversations = useCallback(incoming => {
        if (!incoming?.length) return;
        setConversations(current => {
            const next = mergeConversationList(current, incoming);
            conversationsRef.current = next;
            return next;
        });
        void writeConversationCache(scopeRef.current, incoming);
    }, []);

    const replaceConversations = useCallback(incoming => {
        const nextItems = incoming || [];
        setConversations(current => {
            const incomingIds = new Set(nextItems.map(item => item.id));
            const next = mergeConversationList(
                current.filter(item => incomingIds.has(item.id)),
                nextItems,
            );
            conversationsRef.current = next;
            return next;
        });
        void writeConversationCache(scopeRef.current, nextItems);
    }, []);

    const removeConversations = useCallback(ids => {
        if (!ids?.length) return;
        const removed = new Set(ids.map(Number));
        setConversations(current => {
            const next = current.filter(item => !removed.has(item.id));
            conversationsRef.current = next;
            return next;
        });
        void deleteConversationCache(scopeRef.current, ids);
    }, []);

    const setActiveConversationId = useCallback(conversationId => {
        activeConversationRef.current = Number(conversationId) || null;
        const socket = socketRef.current;
        if (!socket || socket.readyState !== WebSocket.OPEN) return;
        socket.send(JSON.stringify({
            type: 'presence.update',
            app_state: document.visibilityState === 'visible' ? 'FOREGROUND' : 'BACKGROUND',
            current_conversation_id: activeConversationRef.current,
        }));
    }, []);

    const refreshDirectory = useCallback(async signal => {
        const [adminPayload, collaborationPayload] = await Promise.all([
            listChatUsers(signal),
            listChatUsers(signal, 'collaboration'),
        ]);
        setAdminUsers(adminPayload.users || []);
        setUsers(collaborationPayload.users || []);
        setCurrentUser(collaborationPayload.current_user || adminPayload.current_user || null);
        return {...collaborationPayload, admin_users: adminPayload.users || []};
    }, []);

    const refreshUnreadStatus = useCallback(async signal => {
        const payload = await getUnreadStatus(signal);
        setHasUnread(Boolean(payload?.has_unread));
        return Boolean(payload?.has_unread);
    }, []);

    const queryAndMergeSummaries = useCallback(async (ids, signal) => {
        const unique = [...new Set((ids || []).map(Number).filter(Boolean))];
        if (!unique.length) return [];
        const refreshed = [];
        const unavailable = [];
        for (const batch of chunked(unique, 100)) {
            const payload = await queryConversationSummaries(batch, signal);
            refreshed.push(...(payload.conversations || []));
            unavailable.push(...(payload.unavailable_ids || []));
        }
        updateConversations(refreshed);
        removeConversations(unavailable);
        return refreshed;
    }, [removeConversations, updateConversations]);

    const refreshConversations = useCallback(async signal => {
        const ids = conversationsRef.current.map(item => item.id);
        if (!ids.length) {
            return bootstrapPromiseRef.current || [];
        }
        const result = await Promise.all([
            queryAndMergeSummaries(ids, signal),
            refreshUnreadStatus(signal),
        ]);
        return result[0];
    }, [queryAndMergeSummaries, refreshUnreadStatus]);

    const loadMoreConversations = useCallback(async signal => {
        if (!hasMoreConversations || !nextCursor) return [];
        const payload = await listConversationFeed({cursor: nextCursor, signal});
        updateConversations(payload.conversations || []);
        setNextCursor(payload.next_cursor || '');
        setHasMoreConversations(Boolean(payload.has_more));
        return payload.conversations || [];
    }, [hasMoreConversations, nextCursor, updateConversations]);

    const searchConversations = useCallback(async ({query, agentKey, cursor, signal} = {}) => {
        const payload = await listConversationFeed({query, agentKey, cursor, signal});
        updateConversations(payload.conversations || []);
        return payload;
    }, [updateConversations]);

    const loadConversationGroups = useCallback(signal => {
        if (conversationGroupsLoadedRef.current) return Promise.resolve(conversationGroups);
        if (conversationGroupsPromiseRef.current) return conversationGroupsPromiseRef.current;
        const request = (async () => {
            const cached = await readCachedGroups(scopeRef.current);
            if (cached.length) setConversationGroups(cached);
            const payload = await listConversationGroups(signal);
            const groups = payload.groups || [];
            setConversationGroups(groups);
            conversationGroupsLoadedRef.current = true;
            void writeCachedGroups(scopeRef.current, groups);
            return groups;
        })().finally(() => {
            if (conversationGroupsPromiseRef.current === request) {
                conversationGroupsPromiseRef.current = null;
            }
        });
        conversationGroupsPromiseRef.current = request;
        return request;
    }, [conversationGroups]);

    const flushSummaryRefresh = useCallback(async signal => {
        const ids = [...pendingSummaryIdsRef.current];
        pendingSummaryIdsRef.current.clear();
        if (!ids.length) return;
        try {
            await queryAndMergeSummaries(ids, signal);
        } catch (refreshError) {
            if (refreshError?.name !== 'AbortError') setError(refreshError);
        }
    }, [queryAndMergeSummaries]);

    const scheduleSummaryRefresh = useCallback((conversationId, signal) => {
        const id = Number(conversationId) || 0;
        if (!id) return;
        pendingSummaryIdsRef.current.add(id);
        if (summaryRefreshTimerRef.current) return;
        summaryRefreshTimerRef.current = window.setTimeout(() => {
            summaryRefreshTimerRef.current = null;
            void flushSummaryRefresh(signal);
        }, 100);
    }, [flushSummaryRefresh]);

    useEffect(() => {
        let stopped = false;
        let socket = null;
        let reconnectTimer = null;
        let retry = 0;
        const abortController = new AbortController();
        const scope = scopeRef.current;
        const selectedId = routeConversationId();
        let networkBootstrapApplied = false;

        const loadBootstrap = async ({clear = false} = {}) => {
            if (clear) {
                await clearChatCacheScope(scope);
                conversationsRef.current = [];
                setConversations([]);
                setConversationGroups([]);
                conversationGroupsLoadedRef.current = false;
            }
            const payload = await getChatBootstrap({selectedId, signal: abortController.signal});
            if (stopped) return payload;
            networkBootstrapApplied = true;
            const incoming = [...(payload.conversations || [])];
            if (payload.selected_conversation) incoming.push(payload.selected_conversation);
            replaceConversations(incoming);
            setCurrentUser(payload.current_user || null);
            setNextCursor(payload.next_cursor || '');
            setHasMoreConversations(Boolean(payload.has_more));
            setHasUnread(Boolean(payload.has_unread));
            void writeBootstrapCache(scope, payload);
            return payload;
        };

        const cachedPromise = readCachedChat(scope, {selectedId}).then(cached => {
            if (stopped) return cached;
            if (!networkBootstrapApplied) {
                const incoming = [...cached.conversations];
                if (cached.selected) incoming.push(cached.selected);
                replaceConversations(incoming);
                if (cached.meta?.current_user) setCurrentUser(cached.meta.current_user);
                setHasUnread(Boolean(cached.meta?.has_unread));
                setNextCursor(cached.meta?.next_cursor || '');
                setHasMoreConversations(Boolean(cached.meta?.has_more));
            }
            return cached;
        });
        void readCachedGroups(scope).then(groups => {
            if (!stopped && groups.length) setConversationGroups(groups);
        });
        bootstrapPromiseRef.current = Promise.all([cachedPromise, loadBootstrap()])
            .then(([, payload]) => payload)
            .catch(initialError => {
                if (!stopped && initialError?.name !== 'AbortError') setError(initialError);
                return null;
            });

        const processEvent = event => {
            if (!event?.event_id) return;
            if (event.type === 'conversation.access_revoked' && event.conversation_id) {
                removeConversations([event.conversation_id]);
            } else if (event.conversation_id) {
                setInvalidations(current => ({
                    ...current,
                    [event.conversation_id]: (current[event.conversation_id] || 0) + 1,
                }));
                scheduleSummaryRefresh(event.conversation_id, abortController.signal);
                if (event.type?.startsWith('message.')) setHasUnread(true);
            }
        };

        const catchUp = async (watermark, buffered) => {
            let cursor = await readEventCursor(scope);
            let reset = false;
            const events = [];
            if (cursor > 0) {
                do {
                    const page = await syncEvents(cursor, abortController.signal);
                    if (page.reset_required) {
                        reset = true;
                        break;
                    }
                    events.push(...(page.events || []));
                    const next = Number(page.next_event_id || cursor);
                    if (next <= cursor) break;
                    cursor = next;
                    if (!page.has_more && cursor >= watermark) break;
                } while (!stopped);
            }
            if (reset) {
                await loadBootstrap({clear: true});
                cursor = watermark;
            } else if (cursor === 0) {
                await bootstrapPromiseRef.current;
                cursor = watermark;
            } else {
                for (const event of events.toSorted((left, right) => left.event_id - right.event_id)) {
                    processEvent(event);
                }
            }
            for (const event of buffered.splice(0)
                .filter(item => Number(item.event_id) > cursor)
                .toSorted((left, right) => left.event_id - right.event_id)) {
                processEvent(event);
                cursor = event.event_id;
            }
            await writeEventCursor(scope, Math.max(cursor, watermark));
            return Math.max(cursor, watermark);
        };

        const connect = async () => {
            if (stopped) return;
            setConnectionStatus(retry === 0 ? 'connecting' : 'reconnecting');
            try {
                const allocation = await createWebSocketTicket(getChatDeviceId());
                if (stopped) return;
                const buffered = [];
                let syncing = true;
                let cursor = 0;
                socket = new WebSocket(chatWebSocketUrl(), [
                    'cos-chat.v1',
                    `cos-chat-ticket.${allocation.ticket}`,
                ]);
                socketRef.current = socket;
                socket.onmessage = async message => {
                    let frame;
                    try {
                        frame = JSON.parse(message.data);
                    } catch {
                        return;
                    }
                    if (frame.type === 'connection.ready') {
                        retry = 0;
                        setError(null);
                        setConnectionStatus('live');
                        setActiveConversationId(activeConversationRef.current);
                        try {
                            cursor = await catchUp(Number(frame.data?.sync_through_event_id || 0), buffered);
                            syncing = false;
                        } catch (catchUpError) {
                            if (!stopped) {
                                setError(catchUpError);
                                socket?.close();
                            }
                        }
                        return;
                    }
                    if (syncing) {
                        buffered.push(frame);
                        return;
                    }
                    if (frame.event_id > cursor) {
                        processEvent(frame);
                        cursor = frame.event_id;
                        void writeEventCursor(scope, cursor);
                    }
                };
                socket.onclose = () => {
                    if (socketRef.current === socket) socketRef.current = null;
                    if (stopped) return;
                    const delay = Math.min(30000, 1000 * 2 ** retry) + Math.floor(Math.random() * 500);
                    retry += 1;
                    setConnectionStatus('reconnecting');
                    reconnectTimer = window.setTimeout(connect, delay);
                };
                socket.onerror = () => socket?.close();
            } catch (connectError) {
                if (stopped) return;
                setError(connectError);
                const delay = Math.min(30000, 1000 * 2 ** retry) + Math.floor(Math.random() * 500);
                retry += 1;
                setConnectionStatus('reconnecting');
                reconnectTimer = window.setTimeout(connect, delay);
            }
        };

        void connect();
        const handleOnline = () => {
            if (socket?.readyState === WebSocket.OPEN || socket?.readyState === WebSocket.CONNECTING) return;
            window.clearTimeout(reconnectTimer);
            retry = 0;
            void connect();
        };
        const handleVisibility = () => setActiveConversationId(activeConversationRef.current);
        window.addEventListener('online', handleOnline);
        document.addEventListener('visibilitychange', handleVisibility);
        return () => {
            stopped = true;
            abortController.abort();
            window.clearTimeout(reconnectTimer);
            window.clearTimeout(summaryRefreshTimerRef.current);
            window.removeEventListener('online', handleOnline);
            document.removeEventListener('visibilitychange', handleVisibility);
            socketRef.current = null;
            socket?.close(1000, 'page closed');
        };
    }, [removeConversations, replaceConversations, scheduleSummaryRefresh, setActiveConversationId, updateConversations]);

    const createConversation = useCallback(async payload => {
        const response = await createConversationRequest(payload);
        if (response.conversation) updateConversations([response.conversation]);
        return response;
    }, [updateConversations]);

    const ensurePropertyChannel = useCallback(async (propertyId, cosJobId, signal) => {
        const result = await ensurePropertyChannelRequest(propertyId, cosJobId, signal);
        if (result?.conversation) updateConversations([result.conversation]);
        return result;
    }, [updateConversations]);

    const loadConversation = useCallback((conversationId, signal) => {
        const id = Number(conversationId) || 0;
        if (!id) return Promise.resolve(null);
        const existing = conversationsRef.current.find(item => item.id === id);
        if (existing?.details_loaded) return Promise.resolve(existing);
        const inFlight = conversationDetailPromisesRef.current.get(id);
        if (inFlight) return inFlight;
        const request = getConversation(id, signal).then(fullConversation => {
            updateConversations([{...fullConversation, details_loaded: true}]);
            return fullConversation;
        }).finally(() => {
            if (conversationDetailPromisesRef.current.get(id) === request) {
                conversationDetailPromisesRef.current.delete(id);
            }
        });
        conversationDetailPromisesRef.current.set(id, request);
        return request;
    }, [updateConversations]);

    const readConversationMessages = useCallback(conversationId => (
        readCachedMessages(scopeRef.current, conversationId)
    ), []);
    const cacheConversationMessages = useCallback((conversationId, messages) => (
        writeMessageCache(scopeRef.current, conversationId, messages)
    ), []);

    const value = useMemo(() => ({
        users,
        adminUsers,
        currentUser,
        conversations,
        conversationGroups,
        connectionStatus,
        error,
        setError,
        invalidations,
        notifications,
        setActiveConversationId,
        hasUnread,
        unreadCount: hasUnread ? 1 : 0,
        hasMoreConversations,
        refreshDirectory,
        refreshConversations,
        refreshUnreadStatus,
        loadMoreConversations,
        searchConversations,
        loadConversationGroups,
        loadConversation,
        createConversation,
        ensurePropertyChannel,
        readConversationMessages,
        cacheConversationMessages,
    }), [users, adminUsers, currentUser, conversations, conversationGroups, connectionStatus, error,
        invalidations, notifications, hasUnread, hasMoreConversations, refreshDirectory,
        refreshConversations, refreshUnreadStatus, loadMoreConversations, searchConversations,
        loadConversationGroups, loadConversation, createConversation, ensurePropertyChannel,
        readConversationMessages, cacheConversationMessages, setActiveConversationId]);

    return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export function useChat() {
    const value = useContext(ChatContext);
    if (!value) throw new Error('useChat must be used within ChatProvider');
    return value;
}
